import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronUp, Printer, Search } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';

import { dataApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const InsurerGuidelines = () => {
  const { insurerConfigId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [content, setContent] = useState('');
  const [insurerName, setInsurerName] = useState('Insurer Guidelines');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  useEffect(() => {
    const loadGuidelines = async () => {
      setLoading(true);
      setError('');
      try {
        const { config, content: fileContent } = await dataApi.insurerGuidelines(insurerConfigId);
        setInsurerName(config?.name || 'Insurer Guidelines');
        setContent(fileContent || 'No guidelines available for this insurer.');
      } catch (err) {
        console.error('Failed to load insurer guidelines', err);
        setError('Unable to load insurer guidelines.');
        toast.error(err.message || 'Unable to load insurer guidelines.');
      } finally {
        setLoading(false);
      }
    };

    loadGuidelines();
  }, [insurerConfigId]);

  const { matcherSource, matcherFlags, matchCount } = useMemo(() => {
    const safeTerm = escapeRegExp(searchTerm.trim());
    if (!safeTerm) {
      return { matcherSource: null, matcherFlags: null, matchCount: 0 };
    }

    const regex = new RegExp(safeTerm, 'gi');
    const matches = content ? content.match(regex) : null;
    return {
      matcherSource: regex.source,
      matcherFlags: regex.flags,
      matchCount: matches ? matches.length : 0
    };
  }, [content, searchTerm]);

  const highlightText = (text, getIndex) => {
    if (!matcherSource) return text;
    const regex = new RegExp(matcherSource, matcherFlags);
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const hitIndex = getIndex();
      const id = `guideline-hit-${hitIndex}`;
      parts.push(
        <mark
          key={`hit-${hitIndex}`}
          id={id}
          data-hit={hitIndex}
          className="bg-amber-200 text-foreground px-0.5 rounded-sm scroll-mt-28"
        >
          {match[0]}
        </mark>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length ? parts : text;
  };

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchTerm, matchCount]);

  useEffect(() => {
    if (!matchCount) return;
    const target = document.getElementById(`guideline-hit-${currentMatchIndex}`);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('ring', 'ring-amber-400', 'ring-offset-1', 'ring-offset-white');
    const timeout = setTimeout(() => {
      target.classList.remove('ring', 'ring-amber-400', 'ring-offset-1', 'ring-offset-white');
    }, 1200);

    return () => clearTimeout(timeout);
  }, [currentMatchIndex, matchCount]);

  const handlePrevMatch = () => {
    if (!matchCount) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % matchCount);
  };

  const handleNextMatch = () => {
    if (!matchCount) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matchCount);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Insurer Guidelines - ${insurerName}`
  });

  let renderMatchIndex = 0;
  const getNextMatchIndex = () => {
    const current = renderMatchIndex;
    renderMatchIndex += 1;
    return current;
  };

  const renderWithHighlight = (children) => {
    return React.Children.map(children, (child) => {
      if (typeof child === 'string') {
        return highlightText(child, getNextMatchIndex);
      }
      if (Array.isArray(child)) {
        return renderWithHighlight(child);
      }
      if (React.isValidElement(child)) {
        // Avoid re-highlighting mark elements that already carry hits
        if (child.props?.['data-hit'] !== undefined || child.type === 'mark') {
          return child;
        }
        return React.cloneElement(child, {
          children: renderWithHighlight(child.props.children)
        });
      }
      return child;
    });
  };

  return (
    <div className="max-w-full">
      <div className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur-sm print:hidden">
        <div className="w-full mx-auto flex items-center justify-between gap-3 p-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              title="Back"
              className="p-0"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Insurer Guidelines - {insurerName}</h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search guidelines..."
                className="pl-9 pr-24"
              />
              {searchTerm && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white border rounded-md px-1 py-0.5 shadow-sm">
                  {matchCount > 0 && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{currentMatchIndex + 1} / {matchCount}</span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevMatch}
                    disabled={!matchCount}
                    title="Previous match"
                    className="h-7 w-7"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNextMatch}
                    disabled={!matchCount}
                    title="Next match"
                    className="h-7 w-7"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrint}
              title="Print guidelines"
            >
              <Printer className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 pb-12 pt-6 print:pt-0 print:px-0 print:max-w-full">
        <Card ref={printRef} className="shadow-sm print:shadow-none print:border-0">
          <CardContent className="prose lg:prose-lg max-w-6xl mx-auto prose-headings:scroll-mt-24 py-8 lg:py-10 print:py-0 print:px-0 print:text-base print:leading-relaxed">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : error ? (
              <div className="p-4 rounded-md bg-red-50 text-red-700 border border-red-200">
                {error}
              </div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                className="guideline-markdown"
                components={{
                  p: ({ children }) => <p>{renderWithHighlight(children)}</p>,
                  li: ({ children }) => <li>{renderWithHighlight(children)}</li>,
                  h1: ({ children }) => <h1>{renderWithHighlight(children)}</h1>,
                  h2: ({ children }) => <h2>{renderWithHighlight(children)}</h2>,
                  h3: ({ children }) => <h3>{renderWithHighlight(children)}</h3>,
                  h4: ({ children }) => <h4>{renderWithHighlight(children)}</h4>,
                  th: ({ children }) => <th>{renderWithHighlight(children)}</th>,
                  td: ({ children }) => <td>{renderWithHighlight(children)}</td>,
                  tr: ({ children }) => <tr>{children}</tr>,
                  thead: ({ children }) => <thead>{children}</thead>,
                  tbody: ({ children }) => <tbody>{children}</tbody>,
                  table: ({ children }) => <table>{children}</table>,
                  span: ({ children }) => <span>{renderWithHighlight(children)}</span>,
                  strong: ({ children }) => <strong>{renderWithHighlight(children)}</strong>,
                  em: ({ children }) => <em>{renderWithHighlight(children)}</em>,
                  code: ({ inline, className, children, ...props }) => (
                    inline
                      ? <code className={className} {...props}>{renderWithHighlight(children)}</code>
                      : <pre><code className={className} {...props}>{renderWithHighlight(children)}</code></pre>
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InsurerGuidelines;
